'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const DEMO_CREDENTIALS = { email: 'nongki@malang.id', password: 'kopimalang2026' };

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<LoginFormData>({ defaultValues: { rememberMe: false } });

  const copyToClipboard = (field: 'email' | 'password', value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofill = () => {
    setValue('email', DEMO_CREDENTIALS.email);
    setValue('password', DEMO_CREDENTIALS.password);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Berhasil masuk! Selamat nongkrong 🎉');
      router.push('/');
      router.refresh();
    } catch (error: any) {
      setIsLoading(false);
      setError('email', { message: error?.message || 'Email atau password salah' });
    }
  };

  return (
    <div>
      {/* Google Auth */}
      <button className="w-full flex items-center justify-center gap-3 py-3 rounded-xl border border-border bg-card hover:bg-secondary transition-colors text-sm font-600 text-foreground mb-6 btn-press">
        <span className="text-lg">🇬</span>
        Masuk dengan Google
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground font-500">atau</span>
        <div className="flex-1 h-px bg-border" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Email</label>
          <input
            type="email"
            placeholder="kamu@email.com"
            className={`input-field ${errors.email ? 'border-red-400 focus:border-red-400' : ''}`}
            {...register('email', {
              required: 'Email wajib diisi',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Format email tidak valid' },
            })}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1.5 font-500">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-600 text-foreground mb-1.5">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password kamu"
              className={`input-field pr-12 ${errors.password ? 'border-red-400' : ''}`}
              {...register('password', {
                required: 'Password wajib diisi',
                minLength: { value: 6, message: 'Password minimal 6 karakter' },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500 mt-1.5 font-500">{errors.password.message}</p>}
        </div>

        {/* Remember + Forgot */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded accent-accent"
              {...register('rememberMe')}
            />
            <span className="text-sm text-muted-foreground font-500">Ingat saya</span>
          </label>
          <button type="button" className="text-sm font-600 text-accent hover:underline">
            Lupa password?
          </button>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full h-12 text-sm mt-1"
          style={{ minWidth: '160px' }}
        >
          {isLoading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            'Masuk Sekarang'
          )}
        </button>
      </form>

      {/* Demo Credentials Box */}
      <div className="mt-6 p-4 bg-secondary rounded-2xl border border-border">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-700 text-primary uppercase tracking-wider">Demo Account</p>
          <button
            onClick={autofill}
            className="text-xs font-600 text-accent hover:underline px-2 py-1 bg-accent/10 rounded-lg"
          >
            Isi Otomatis
          </button>
        </div>
        <div className="flex flex-col gap-2">
          {[
            { field: 'email' as const, label: 'Email', value: DEMO_CREDENTIALS.email },
            { field: 'password' as const, label: 'Password', value: DEMO_CREDENTIALS.password },
          ].map((item) => (
            <div key={`cred-${item.field}`} className="flex items-center justify-between bg-card rounded-xl px-3 py-2.5 border border-border">
              <div>
                <p className="text-[10px] text-muted-foreground font-500 uppercase tracking-wider">{item.label}</p>
                <p className="text-sm font-600 text-foreground font-tabular">{item.value}</p>
              </div>
              <button
                onClick={() => copyToClipboard(item.field, item.value)}
                className="p-1.5 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-primary"
              >
                {copiedField === item.field ? <Check size={14} className="text-green-DEFAULT" /> : <Copy size={14} />}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}